import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Package, Layers, Newspaper, FileText, MessageSquare,
  Briefcase, Award, HelpCircle, Building2, Settings, LogOut, Inbox, ChevronRight,
  Home, Info,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/page-home", label: "Home Page", icon: Home },
  { to: "/admin/page-about", label: "About Page", icon: Info },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: Layers },
  { to: "/admin/industries", label: "Industries", icon: Building2 },
  { to: "/admin/blog", label: "Blog Posts", icon: Newspaper },
  { to: "/admin/case-studies", label: "Case Studies", icon: Briefcase },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquare },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/certifications", label: "Certifications", icon: Award },
  { to: "/admin/pages", label: "Static Pages", icon: FileText },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login", { replace: true });
  }, [loading, user, navigate]);

  if (loading) return <div className="min-h-screen bg-[#1C1C1C] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C1121F] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return null;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#1C1C1C] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="h-section text-white">Access denied</h1>
          <p className="mt-3 font-body text-white/60">Your account ({user.email}) is not authorized for the admin panel.</p>
          <button onClick={logout} className="btn-primary mt-6">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid grid-cols-1 md:grid-cols-[260px_1fr] font-body" data-testid="admin-layout">
      <aside className="bg-[#1C1C1C] border-r border-[#333] flex flex-col md:h-screen md:sticky md:top-0">
        <div className="p-6 border-b border-[#333]">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <div className="w-9 h-9 bg-[#C1121F] flex items-center justify-center"><span className="text-white font-heading font-bold">D</span></div>
            <div>
              <div className="font-heading font-bold text-white text-sm">DURGA</div>
              <div className="font-heading text-[10px] tracking-[0.18em] text-[#C1121F]">ADMIN PANEL</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm font-heading font-medium transition-colors border-l-2 ${isActive ? "bg-[#0A0A0A] text-white border-[#C1121F]" : "text-white/60 hover:text-white hover:bg-[#0A0A0A] border-transparent"}`}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#333]">
          <div className="flex items-center gap-3 mb-3">
            {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 bg-[#333]" />}
            <div className="text-xs">
              <div className="font-heading font-semibold text-white">{user.name}</div>
              <div className="text-white/50">{user.email}</div>
            </div>
          </div>
          <button onClick={logout} className="w-full text-xs font-heading font-medium text-white/60 hover:text-[#C1121F] flex items-center gap-2 px-2 py-1" data-testid="admin-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="bg-[#F5F5F5] text-[#1C1C1C] min-h-screen">
        <div className="p-6 lg:p-10 max-w-6xl mx-auto"><Outlet /></div>
      </main>
    </div>
  );
}
